import { ArticleItem, Box, Button, styled } from "@tm/components"
import { useLocalization } from "@tm/localization"
import { ArticleListSortingMode } from "@tm/models"
import { useCallback, useMemo, useRef } from "react"
import { ArticleGroup } from "../components/ArticleGroup"
import { ArticleListWrapper } from "../components/ArticleListWrapper"
import { ArticleSelection } from "../components/ArticleSelection"
import { NextPageLoader } from "../components/NextPageLoader"
import { NoSearchResult } from "../components/NoSearchResult"
import { useListOptionsContext } from "../ContextProvider"
import { useProductGroupTopicIdsDataContext } from "../ContextProvider/ProductGroupTopicIds"
import { usePartsViewOptions } from "../hooks/usePartsViewOptions"
import { useProductGroupsArticlesContext } from "./ContextProvider/Articles"
import { useProductGroupsListParamsContext } from "./ContextProvider/ListParams"
import { useResizeErpColumnInDefaultArticleItem } from "../hooks/useResizeErpContainerColumn"
import { OePositions } from "../components/OePositions"
import { ArticleGroupHeader } from "../../list/components/ArticleGroupHeader/ArticleGroupHeader"
import { ArticleListFeaturesDisableProvider } from "../ArticleListConfiguration/ArticleListFeaturesDisable"
import { OeArticleItemStateProvider } from "../components/OeArticle/OeArticleItemStateProvider"
import { useArticleSelection } from "../hooks"
import { CrossSelling, useCrossSellingActions } from "../components/CrossSelling"
import { usePartsAlternativesModuleState } from "../PartsAlternatives/PartsAlternativesModuleState"

const ModalArticleListWrapper = styled(ArticleListWrapper)({ height: "100%", paddingLeft: 0, paddingRight: 1, margin: 1, overflow: "hidden" })

export default function ProductGroupsList() {
    const { translateText } = useLocalization()

    const isAlternativeSearch = usePartsAlternativesModuleState((x) => !!x)

    const {
        sorting: { productGroups: sortingProductGroups, setForProductGroups: activateSortingForProductGroups },
        noResult,
        attributes,
        toggleAttribute,
        setAttributes,
        oePositions,
        selectedOePart,
        changeOePosition,
        listType,
        startParams: { crossSellingModalType },
    } = useProductGroupsListParamsContext()

    const {
        articleGroups,
        isLoading,
        isLoaded,
        isFailed,
        hasNextPage,
        loadNextPage,
        isStalling,
        isFrontendFiltered,
        providedArticlesGroup,
        providedArticlesGroupLoaded,
    } = useProductGroupsArticlesContext()

    const options = useListOptionsContext()
    const productGroupTopicIds = useProductGroupTopicIdsDataContext()
    const { selectedArticles } = useArticleSelection()

    const crossSellingActions = useCrossSellingActions(listType, isAlternativeSearch, crossSellingModalType)

    const {
        partsViewSettings: { showVehicleRecordsFilters },
    } = usePartsViewOptions()

    const mainContainerRef = useRef<HTMLElement>(null)
    useResizeErpColumnInDefaultArticleItem(mainContainerRef.current, !!articleGroups.length)

    const usedAttributeFilters = useMemo(() => attributes.map((x) => x.query ?? `${x.id}|${x.key ?? ""}`), [attributes])

    const handleResetAttributeFilters = useCallback(() => {
        if (usedAttributeFilters.length) {
            setAttributes([])
        }
    }, [usedAttributeFilters, setAttributes])

    const handleChangeSorting = useCallback(
        (productGroupId: number, value: ArticleListSortingMode | undefined) => {
            activateSortingForProductGroups([productGroupId], value)
        },
        [activateSortingForProductGroups]
    )

    const onAddToBasket = () => {
        crossSellingActions.loadRecommendation(selectedArticles)
    }

    const showPageLoader = ((hasNextPage || isLoading) && !isFrontendFiltered) || isStalling

    const nextGroupOffset =
        providedArticlesGroup && Array.isArray(providedArticlesGroup)
            ? providedArticlesGroup.reduce((acc, group) => acc + (group.articles?.length ?? 0), 0)
            : 0

    return (
        <ModalArticleListWrapper>
            <Box ref={mainContainerRef} sx={{ display: "flex", flexDirection: "column", position: "relative" }}>
                <ArticleSelection showArticleComparision onAddToBasket={onAddToBasket} hideBasketButton={options.hideAddToBasketButton} />

                {oePositions && (
                    <Box p={1} className="oe-article-section">
                        <Box marginBottom={1}>
                            <OePositions onPositionSelect={changeOePosition} positions={oePositions} />
                        </Box>
                        {selectedOePart && (
                            <>
                                <ArticleGroupHeader title={translateText(803)} key="oeSelectedPartHeader" />
                                <ArticleListFeaturesDisableProvider disableSelection>
                                    <OeArticleItemStateProvider article={selectedOePart} articleTypeKey="OePositionsSelectedOePart">
                                        <ArticleItem variant="OE" />
                                    </OeArticleItemStateProvider>
                                </ArticleListFeaturesDisableProvider>
                            </>
                        )}
                    </Box>
                )}

                <CrossSelling listType={listType} newSearchLoaded={isLoaded} crossSellingModalType={crossSellingModalType} />

                {providedArticlesGroupLoaded &&
                    providedArticlesGroup &&
                    providedArticlesGroup.map((group, groupIndex) => {
                        const groupOffset = providedArticlesGroup
                            .slice(0, groupIndex)
                            .reduce((acc, prevGroup) => acc + (prevGroup.articles?.length || 0), 0)

                        if (!group.articles.length) {
                            return null
                        }

                        return (
                            <ArticleGroup
                                groupOffset={groupOffset}
                                key={group.productGroup.id}
                                group={group}
                                currentSorting={sortingProductGroups[group.productGroup.id]}
                                onChangeSorting={handleChangeSorting}
                                showFilterOptions={showVehicleRecordsFilters}
                                usedAttributeFilters={usedAttributeFilters}
                                onToggleAttributeFilter={toggleAttribute}
                                onResetAttributeFilters={handleResetAttributeFilters}
                                productGroupTopicIds={productGroupTopicIds}
                            />
                        )
                    })}

                {(isLoaded || !isLoading) && (noResult || (isFailed && !showPageLoader) || !articleGroups.length) && (
                    <NoSearchResult searchType="direct" order={nextGroupOffset + 1} />
                )}

                {isLoaded &&
                    !isStalling &&
                    articleGroups.map((group, groupIndex) => {
                        const groupOffset =
                            nextGroupOffset +
                            2 +
                            articleGroups.slice(0, groupIndex).reduce((acc, prevGroup) => acc + (prevGroup.articles?.length || 0), 0)

                        return (
                            <ArticleGroup
                                groupOffset={groupOffset}
                                key={group.productGroup.id}
                                group={group}
                                currentSorting={sortingProductGroups[group.productGroup.id]}
                                onChangeSorting={handleChangeSorting}
                                showFilterOptions={showVehicleRecordsFilters}
                                usedAttributeFilters={usedAttributeFilters}
                                onToggleAttributeFilter={toggleAttribute}
                                onResetAttributeFilters={handleResetAttributeFilters}
                                productGroupTopicIds={productGroupTopicIds}
                            />
                        )
                    })}

                {showPageLoader && (
                    <NextPageLoader
                        loadNextPage={loadNextPage}
                        canLoadNextArticles={hasNextPage}
                        skeletonArticleCount={articleGroups.length ? 1 : 5}
                        skeletonCategory
                    />
                )}

                {hasNextPage && isFrontendFiltered && (
                    <Box display="flex" justifyContent="center">
                        <Button color="highlight" onClick={loadNextPage}>
                            {translateText(12430)}
                        </Button>
                    </Box>
                )}
            </Box>
        </ModalArticleListWrapper>
    )
}
